// ─── Engine: which model, how fast it answers, what it costs ────────────────
//
// Design rules applied (ui-ux-pro-max):
//  · Money and counts use tabular figures so they don't jitter as they change.
//  · Cost is shown in USD because that is the currency OpenRouter bills in —
//    converting to MAD here would invent a rate and go stale.
//  · The figures are what OpenRouter actually charged, not a local estimate.
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { whatsappApi } from '@/lib/api';
import { useT } from '@/lib/i18n';

const inputCls =
  'w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white transition-colors duration-200 ' +
  'focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent';

/** Fractions of a cent are noise; sub-cent totals still need to be visible. */
function usd(n: number): string {
  if (!Number.isFinite(n)) return '$0';
  if (n === 0) return '$0';
  if (n < 0.01) return '< $0.01';
  return `$${n.toFixed(2)}`;
}

function Tile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 px-4 py-3.5">
      <p className="text-[10.5px] font-semibold text-gray-400 uppercase tracking-wide">{label}</p>
      <p className="text-[22px] leading-tight font-bold text-gray-900 tabular-nums mt-0.5">{value}</p>
      {hint && <p className="text-[10.5px] text-gray-400 mt-0.5">{hint}</p>}
    </div>
  );
}

export function EngineCard() {
  const { t } = useT();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['assistant-usage'], queryFn: () => whatsappApi.usage() });

  const [model, setModel] = useState('');
  const [delay, setDelay] = useState('12');

  // Seed the controls once the server's values arrive, without clobbering an
  // edit in progress on a later refetch.
  useEffect(() => {
    if (!data) return;
    setModel((m) => m || data.current_model);
    setDelay((d) => (d === '12' ? String(data.reply_delay_seconds) : d));
  }, [data]);

  const saveMut = useMutation({
    mutationFn: () => whatsappApi.saveSettings({ assistant_model: model, reply_delay_seconds: Number(delay) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['assistant-usage'] }),
  });

  if (isLoading || !data) return <p className="text-[12.5px] text-gray-400">{t('Chargement…')}</p>;

  const dirty = model !== data.current_model || Number(delay) !== data.reply_delay_seconds;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label={t('Coût aujourd’hui')} value={usd(data.cost_today_usd)} />
        <Tile label={t('30 derniers jours')} value={usd(data.cost_30d_usd)} />
        <Tile label={t('Réponses')} value={String(data.calls_30d)} hint={t('30 jours')} />
        <Tile label={t('Jetons')} value={data.tokens_30d.toLocaleString('fr-FR')} hint={t('30 jours')} />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h2 className="text-sm font-semibold text-gray-800 mb-1">{t('Modèle')}</h2>
        <p className="text-[11.5px] text-gray-500 mb-3 leading-snug">
          {t('Tous ces modèles comprennent le texte, les images et les messages vocaux. Changer de modèle prend effet immédiatement, sans redéploiement.')}
        </p>
        <div className="space-y-2">
          {data.models.map((m) => (
            <label key={m.id}
              className={`flex items-start gap-2.5 p-3 rounded-lg border cursor-pointer transition-colors duration-200 ${
                model === m.id ? 'border-brand-400 bg-brand-50' : 'border-gray-200 hover:border-gray-300'
              }`}>
              <input type="radio" name="assistant-model" className="mt-0.5 accent-brand-600"
                checked={model === m.id} onChange={() => setModel(m.id)} />
              <span>
                <span className="block text-[12.5px] font-semibold text-gray-800">{m.label}</span>
                <span className="block text-[11px] text-gray-500 tabular-nums">{m.note}</span>
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h2 className="text-sm font-semibold text-gray-800 mb-1">{t('Attendre avant de répondre')}</h2>
        <p className="text-[11.5px] text-gray-500 mb-3 leading-snug">
          {t('Beaucoup de clients envoient 2 ou 3 messages d’affilée. L’assistant attend ce délai après le dernier message, puis répond UNE seule fois en tenant compte de tout. Mettre 0 pour répondre tout de suite.')}
        </p>
        <div className="flex items-center gap-2 max-w-[220px]">
          <input className={inputCls} type="number" min="0" max="60" value={delay}
            onChange={(e) => setDelay(e.target.value)} />
          <span className="text-[12px] text-gray-500 shrink-0">{t('secondes')}</span>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button size="sm" onClick={() => saveMut.mutate()} loading={saveMut.isPending} disabled={!dirty}>
          {t('Enregistrer')}
        </Button>
        {saveMut.isSuccess && !dirty && <span className="text-[11.5px] text-green-600">{t('✓ Enregistré')}</span>}
        {saveMut.isError && <span className="text-[11.5px] text-red-600">{(saveMut.error as Error).message}</span>}
      </div>

      {data.by_model.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-sm font-semibold text-gray-800 mb-2.5">{t('Détail par modèle (30 jours)')}</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="text-left text-gray-400 text-[10.5px] uppercase tracking-wide">
                  <th className="pb-1.5 font-semibold">{t('Modèle')}</th>
                  <th className="pb-1.5 font-semibold text-right">{t('Appels')}</th>
                  <th className="pb-1.5 font-semibold text-right">{t('Coût')}</th>
                </tr>
              </thead>
              <tbody>
                {data.by_model.map((m) => (
                  <tr key={m.model} className="border-t border-gray-100">
                    <td className="py-1.5 text-gray-700">{m.model}</td>
                    <td className="py-1.5 text-right tabular-nums text-gray-600">{m.calls}</td>
                    <td className="py-1.5 text-right tabular-nums text-gray-800 font-medium">{usd(m.cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
